import { Link } from 'react-router-dom';
import { motion } from '../lib/motion';
import type { HomepageContent } from '../lib/types';
import { REGISTRE } from '../data/registre';
import { RACINE_ZONES } from '../data/communes';
import { cheminDestination, destinationsOrdonnees } from '../data/destinations';
import { estPubliable } from '../data/pages-en-preparation';

/**
 * Les neuf cartes : la Belgique, puis les huit pays de la table
 * `destinations` (lot 17), dans l'ordre du menu.
 *
 * C4 du plan correctif : trois listes de pays coexistaient sur le site. La
 * liste vient du registre (`couverture.pays`) ; la table `destinations` doit
 * lui être identique, et le validateur du build le vérifie. Les drapeaux
 * viennent de la table : une donnée de présentation, éditable en back-office.
 *
 * Chaque carte est un LIEN — c'est le lien entrant le plus fort du site vers
 * les pages pays — mais seulement vers une page qui existe : la Belgique vers
 * l'index des zones, un pays vers sa page si elle est publiée. Une destination
 * non publiée reste une carte sans lien, jamais un lien mort.
 */
const defaultDestinations = [
  {
    flag: '\uD83C\uDDE7\uD83C\uDDEA',
    country: 'Belgique',
    desc: REGISTRE['couverture.belgique'].court,
    featured: true,
  },
  ...destinationsOrdonnees().map((d) => ({
    flag: d.drapeau,
    country: d.paysNom,
    desc: `Déménagement Belgique\u2013${d.paysNom}`,
    featured: false,
  })),
];

/** La page d'une carte, si elle existe : l'index des zones, ou la page pays publiée. */
function lienDeCarte(country: string): string | null {
  if (country === 'Belgique') return RACINE_ZONES;
  const d = destinationsOrdonnees().find((x) => x.paysNom === country);
  if (!d) return null;
  const chemin = cheminDestination(d.slug);
  return estPubliable(chemin) ? chemin : null;
}

interface Props {
  data?: HomepageContent['service_area'] | null;
}

/**
 * Découpe le texte du bandeau autour de la PREMIÈRE occurrence de « GRAMME ».
 *
 * La mise en gras portait auparavant sur cette seule occurrence — `String.replace`
 * avec une chaîne ne remplace que la première. Le comportement est conservé à
 * l'identique : corriger une faille ne doit pas changer ce qui s'affiche.
 */
function segmentsDuBandeau(texte: string): Array<{ texte: string; gras: boolean }> {
  const MARQUE = 'GRAMME';
  const i = texte.indexOf(MARQUE);
  if (i === -1) return [{ texte, gras: false }];
  return [
    { texte: texte.slice(0, i), gras: false },
    { texte: MARQUE, gras: true },
    { texte: texte.slice(i + MARQUE.length), gras: false },
  ].filter((seg) => seg.texte.length > 0);
}

export default function ServiceArea({ data }: Props) {
  const title = data?.title || 'Nous intervenons partout';
  const subtitle = data?.subtitle || 'Local, national ou international, vos biens sont entre de bonnes mains';
  const destinations = data?.destinations || defaultDestinations;
  const bannerText = data?.banner_text || "Besoin d'un déménagement rapide ? GRAMME intervient dans des délais ultra serrés.";

  return (
    <section
      id="zones"
      className="relative py-16 md:py-24 overflow-hidden"
      style={{
        background: `
          linear-gradient(rgba(19,32,115,0.97), rgba(19,32,115,0.97)),
          repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(255,255,255,0.03) 40px, rgba(255,255,255,0.03) 41px)
        `,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block bg-yellow text-navy text-[11px] uppercase tracking-[0.2em] font-bold rounded-full px-4 py-1.5 mb-4">
            ZONES D'INTERVENTION
          </span>
          <h2 className="text-3xl md:text-4xl font-black uppercase text-white mb-3">
            {title}
          </h2>
          <p className="text-white/70 text-lg max-w-xl mx-auto">
            {subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
          {destinations.map((dest, i) => {
            const lien = lienDeCarte(dest.country);
            const classes = `block h-full rounded-xl p-6 border transition-all duration-200 hover:bg-white/20 hover:scale-[1.02] ${
              dest.featured
                ? 'bg-white/15 border-white/20 border-l-4 border-l-yellow'
                : 'bg-white/10 border-white/20'
            } ${lien ? 'cursor-pointer' : 'cursor-default'}`;
            const contenu = (
              <>
                <span className="text-2xl leading-none block mb-2" aria-hidden="true">{dest.flag}</span>
                <h3 className="text-white font-bold text-base">{dest.country}</h3>
                <p className="text-white/70 text-sm mt-1">{dest.desc}</p>
              </>
            );
            return (
              <motion.div
                key={dest.country}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
              >
                {/* Ancre = nom du pays + « Déménagement Belgique–<Pays> », le
                    libellé de la carte. Une carte sans page reste un bloc. */}
                {lien ? <Link to={lien} className={classes}>{contenu}</Link> : <div className={classes}>{contenu}</div>}
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white/10 rounded-xl p-6 flex flex-col md:flex-row items-center justify-between gap-4"
        >
          {/* ÉTAIT UN `dangerouslySetInnerHTML`, ET C'ÉTAIT UNE PORTE OUVERTE.
              ─────────────────────────────────────────────────────────────────
              Le texte venait de `banner_text`, éditable depuis le back-office
              (« Texte bannière », éditeur de page). Il était injecté comme HTML
              pour mettre « GRAMME » en gras — donc TOUT ce qu'il contenait
              était interprété comme du HTML.

              Un `<img src=x onerror="…">` saisi dans ce champ s'exécutait sur
              la page d'accueil, dans l'origine du site, avec accès aux sessions
              des visiteurs. La CSP n'y voyait rien : c'est notre propre code
              qui l'écrivait.

              Le gras ne vaut pas ça, et il ne le coûte pas : en JSX, React pose
              du texte comme du texte et l'élément `<strong>` comme un élément.
              Il n'y a plus de point d'écriture dangereux — rien à assainir,
              rien à autoriser par une politique Trusted Types. */}
          <p className="text-white text-sm md:text-base">
            {segmentsDuBandeau(bannerText).map((seg, i) =>
              seg.gras ? <strong key={i}>{seg.texte}</strong> : <span key={i}>{seg.texte}</span>,
            )}
          </p>
          {/* Était un <button onClick={scrollTo}>. Même défaut que la
              navigation : l'appel à l'action le plus visible de la section
              n'apparaissait pas comme un lien dans l'arbre d'accessibilité.
              Puis un lien vers `/#contact`, c'est-à-dire un retour vers
              l'accueil : il mène désormais à la page de conversion. */}
          <Link
            to="/contact-devis"
            className="bg-yellow text-navy font-bold uppercase py-3 px-6 rounded-xl hover:bg-white transition-colors duration-200 whitespace-nowrap"
          >
            Contactez-nous
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
